import React, { useState, useEffect, useCallback } from 'react';
import api from '../../config/axios';
import Header from '../common/Header';
import './Report.css';
import { useNavigate } from 'react-router-dom';

interface ReportProps {
  user: any;
}

interface ReportData {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  topSellingItems: Array<{
    name: string;
    quantity: number;
    revenue: number;
    imageUrl: string;
  }>;
  dailyRevenue: Array<{
    date: string;
    revenue: number;
    orders: number;
  }>;
  hourlyRevenue: Array<{
    hour: number;
    revenue: number;
    orders: number;
  }>;
}

interface Pnl {
  revenue: number;
  orderCount: number;
  purchaseExpense: number;
  cogs: number;
  grossProfit: number;
}

const Report: React.FC<ReportProps> = ({ user }) => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [pnl, setPnl] = useState<Pnl | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const navigate = useNavigate();

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/reports`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          type: reportType
        }
      });
      setReportData(response.data.data);
      if (user.role === 'admin') {
        try {
          const p = await api.get('/api/reports/pnl', {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          });
          setPnl(p.data.data);
        } catch {
          setPnl(null);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tải báo cáo');
    } finally {
      setLoading(false);
    }
  }, [dateRange, reportType, user.role]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleReportTypeChange = (type: 'daily' | 'weekly' | 'monthly') => {
    setReportType(type);
    const today = new Date();
    let startDate = new Date();
    
    switch (type) {
      case 'daily':
        startDate = new Date(today.getTime() - 1 * 24 * 60 * 60 * 1000);
        break;
      case 'weekly':
        startDate = new Date(today.getTime() - 1 * 7 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        break;
    }
    
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    });
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('vi-VN') + ' ₫';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const onLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  return (
    <>
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{user.role === 'admin' ? 'Quản lý' : 'Nhân viên'}</span>
      </Header>
      <div className="report-container">
        <div className="report-header">
          <button
            className="report-back-btn"
            onClick={() => navigate('/')}
            title="Quay lại Dashboard"
          >
            ←
          </button>
          <h2 className="report-title">BÁO CÁO DOANH THU</h2>
        </div>

        {error && <div className="report-error">{error}</div>}
        {loading && <div className="report-loading">Đang tải báo cáo...</div>}

        <div className="report-filters">
          <div className="report-type-selector">
            <button
              className={`report-type-btn ${reportType === 'daily' ? 'active' : ''}`}
              onClick={() => handleReportTypeChange('daily')}
            >
              Theo ngày
            </button>
            <button
              className={`report-type-btn ${reportType === 'weekly' ? 'active' : ''}`}
              onClick={() => handleReportTypeChange('weekly')}
            >
              Theo tuần
            </button>
            <button
              className={`report-type-btn ${reportType === 'monthly' ? 'active' : ''}`}
              onClick={() => handleReportTypeChange('monthly')}
            >
              Theo tháng
            </button>
          </div>
          
          <div className="report-date-range">
            <div className="date-input-group">
              <label>Từ ngày:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                className="report-date-input"
              />
            </div>
            <div className="date-input-group">
              <label>Đến ngày:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                className="report-date-input"
              />
            </div>
          </div>
        </div>

        {reportData && (
          <div className="report-overview">
            <div className="report-card">
              <div className="report-card-icon">💰</div>
              <div className="report-card-content">
                <h3>Tổng doanh thu</h3>
                <p className="report-card-value">{formatCurrency(reportData.totalRevenue)}</p>
              </div>
            </div>
            
            <div className="report-card">
              <div className="report-card-icon">📋</div>
              <div className="report-card-content">
                <h3>Tổng đơn hàng</h3>
                <p className="report-card-value">{reportData.totalOrders.toLocaleString()}</p>
              </div>
            </div>
            
            <div className="report-card">
              <div className="report-card-icon">📊</div>
              <div className="report-card-content">
                <h3>Giá trị TB/đơn</h3>
                <p className="report-card-value">{formatCurrency(reportData.averageOrderValue)}</p>
              </div>
            </div>
          </div>
        )}

        {reportData && reportData.totalOrders === 0 && (
          <div className="report-no-data">
            <div className="report-no-data-icon">📊</div>
            <h3>Không có dữ liệu</h3>
          </div>
        )}

        {pnl && (
          <div className="report-section">
            <h3 className="report-section-title">Tài chính (lãi / lỗ)</h3>
            <div className="report-pnl">
              <div className="report-pnl-card revenue">
                <span>Doanh thu</span>
                <b>{formatCurrency(pnl.revenue)}</b>
              </div>
              <div className="report-pnl-card cogs">
                <span>Giá vốn hàng bán</span>
                <b>{formatCurrency(pnl.cogs)}</b>
              </div>
              <div className={`report-pnl-card ${pnl.grossProfit >= 0 ? 'profit' : 'loss'}`}>
                <span>Lợi nhuận gộp</span>
                <b>{formatCurrency(pnl.grossProfit)}</b>
              </div>
              <div className="report-pnl-card expense">
                <span>Chi phí nhập kho</span>
                <b>{formatCurrency(pnl.purchaseExpense)}</b>
              </div>
            </div>
            <p className="report-pnl-note">
              Lợi nhuận gộp = Doanh thu − Giá vốn hàng bán. Chi phí nhập kho là tiền bỏ ra mua nguyên liệu trong kỳ (dòng tiền), tính riêng.
            </p>
          </div>
        )}

        {reportData && (
          <div className="report-section">
            <h3 className="report-section-title">Doanh thu và đơn hàng theo thời gian</h3>
            <div className="report-chart">
              {reportData.dailyRevenue.map((item, index) => {
                const maxRevenue = Math.max(...reportData.dailyRevenue.map(d => d.revenue));
                const maxOrders = Math.max(...reportData.dailyRevenue.map(d => d.orders));
                const isMaxRevenue = item.revenue === maxRevenue && maxRevenue > 0;
                const isMaxOrders = item.orders === maxOrders && maxOrders > 0;
                const revenueHeight = maxRevenue > 0 ? Math.max((item.revenue / maxRevenue) * 200, 3) : (item.revenue > 0 ? 3 : 0);
                const ordersHeight = maxOrders > 0 ? Math.max((item.orders / maxOrders) * 200, 3) : (item.orders > 0 ? 3 : 0);
                return (
                  <div key={index} className="chart-bar">
                    <div className="chart-bars-container">
                      <div 
                        className={`chart-bar-fill chart-bar-revenue${isMaxRevenue ? ' chart-bar-max' : ''}`}
                        style={{ 
                          height: `${revenueHeight}%`,
                          minHeight: item.revenue > 0 ? '3px' : '0px',
                          border: isMaxRevenue ? '2.5px solid #225ea8' : undefined,
                          boxShadow: isMaxRevenue ? '0 0 8px #225ea855' : undefined,
                          position: 'relative',
                          marginRight: '2px'
                        }}
                        title={`Doanh thu: ${formatCurrency(item.revenue)}`}
                      >
                      </div>
                      <div 
                        className={`chart-bar-fill chart-bar-orders${isMaxOrders ? ' chart-bar-max' : ''}`}
                        style={{ 
                          height: `${ordersHeight}%`,
                          minHeight: item.orders > 0 ? '3px' : '0px',
                          border: isMaxOrders ? '2.5px solid #2f855a' : undefined,
                          boxShadow: isMaxOrders ? '0 0 8px #2f855a55' : undefined,
                          position: 'relative',
                          marginLeft: '2px'
                        }}
                        title={`Đơn hàng: ${item.orders} đơn`}
                      >
                      </div>
                    </div>
                    <div className="chart-bar-label">
                      <div className="chart-date">{formatDate(item.date)}</div>
                      <div className="chart-value">{formatCurrency(item.revenue)}</div>
                      <div className="chart-orders">{item.orders} đơn</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color legend-revenue"></div>
                <span>Doanh thu</span>
              </div>
              <div className="legend-item">
                <div className="legend-color legend-orders"></div>
                <span>Số đơn hàng</span>
              </div>
            </div>
          </div>
        )}

        {reportData && reportData.topSellingItems.length > 0 && (
          <div className="report-section">
            <h3 className="report-section-title">Món bán chạy</h3>
            <div className="top-items-list">
              {reportData.topSellingItems.slice(0, 3).map((item, index) => (
                <div key={index} className="top-item-card">
                  <div className="top-item-rank">#{index + 1}</div>
                  <img src={item.imageUrl} alt={item.name} className="top-item-image" />
                  <div className="top-item-info">
                    <h4 className="top-item-name">{item.name}</h4>
                    <p className="top-item-stats">
                      Đã bán: {item.quantity} phần | Doanh thu: {formatCurrency(item.revenue)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {reportData && (
          <div className="report-section">
            <h3 className="report-section-title">Doanh thu và đơn hàng theo giờ trong ngày</h3>
            <div className="hourly-chart">
              {reportData.hourlyRevenue.filter(item => item.hour >= 8 && item.hour <= 22).map((item, index) => {
                const maxRevenue = Math.max(...reportData.hourlyRevenue.map(h => h.revenue));
                const maxOrders = Math.max(...reportData.hourlyRevenue.map(h => h.orders));
                const isMaxRevenue = item.revenue === maxRevenue && maxRevenue > 0;
                const isMaxOrders = item.orders === maxOrders && maxOrders > 0;
                const revenueHeight = maxRevenue > 0 ? Math.max((item.revenue / maxRevenue) * 200, 3) : (item.revenue > 0 ? 3 : 0);
                const ordersHeight = maxOrders > 0 ? Math.max((item.orders / maxOrders) * 200, 3) : (item.orders > 0 ? 3 : 0);
                return (
                  <div key={index} className="hourly-bar">
                    <div className="hourly-bars-container">
                      <div 
                        className={`hourly-bar-fill hourly-bar-revenue${isMaxRevenue ? ' hourly-bar-max' : ''}`}
                        style={{ 
                          height: `${revenueHeight}%`,
                          minHeight: item.revenue > 0 ? '3px' : '0px',
                          border: isMaxRevenue ? '2.5px solid #225ea8' : undefined,
                          boxShadow: isMaxRevenue ? '0 0 8px #225ea855' : undefined,
                          position: 'relative',
                          marginRight: '2px'
                        }}
                        title={`Doanh thu: ${formatCurrency(item.revenue)}`}
                      >
                      </div>
                      <div 
                        className={`hourly-bar-fill hourly-bar-orders${isMaxOrders ? ' hourly-bar-max' : ''}`}
                        style={{ 
                          height: `${ordersHeight}%`,
                          minHeight: item.orders > 0 ? '3px' : '0px',
                          border: isMaxOrders ? '2.5px solid #2f855a' : undefined,
                          boxShadow: isMaxOrders ? '0 0 8px #2f855a55' : undefined,
                          position: 'relative',
                          marginLeft: '2px'
                        }}
                        title={`Đơn hàng: ${item.orders} đơn`}
                      >
                      </div>
                    </div>
                    <div className="hourly-label">
                      <div className="hourly-time">{item.hour}:00</div>
                      <div className="hourly-value">{formatCurrency(item.revenue)}</div>
                      <div className="hourly-orders">{item.orders} đơn</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color legend-revenue"></div>
                <span>Doanh thu</span>
              </div>
              <div className="legend-item">
                <div className="legend-color legend-orders"></div>
                <span>Số đơn hàng</span>
              </div>
            </div>
          </div>
        )}

        <div className="report-actions">
          <button className="report-export-btn" onClick={() => window.print()}>
            📄 Xuất báo cáo
          </button>
        </div>
      </div>
    </>
  );
};

export default Report;